import React, { useState } from 'react';
import {
    Form,
    FormFeedback,
    Input,
    Label,
    Modal,
    ModalBody,
    ModalHeader,
    Nav,
    NavItem,
    NavLink,
    Spinner,
    TabContent,
    TabPane,
} from 'reactstrap';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';
import TableContainer from '../.././components/TableContainerReactTable';
import { Placement } from '../../types/placement';
import numeral from 'numeral';
import { confirmImportPlacements, importPlacements } from '../../api/placementApi';
import { useProfile } from '../../hooks/UserHooks';

interface ImportPlacementModalProps {
    isOpen: boolean;
    toggle: () => void;
    onAddData: (newData: Placement[]) => void;
}

const ImportPlacementModal = ({ isOpen, toggle, onAddData }: ImportPlacementModalProps) => {
    const { userProfile } = useProfile();
    const [activeArrowTab, setActiveArrowTab] = useState<number>(1);
    const [firstStepImportButtonLoader, setFirstStepImportButtonLoader] = useState<boolean>(false);
    const [secondStepImportButtonLoader, setSecondStepImportButtonLoader] = useState<boolean>(false);
    const [placementsToSave, setPlacementsToSave] = useState<(Placement & { errors: string[]; warnings: string[] })[]>([]);
    numeral.defaultFormat('0b');

    const toggleImportPlacementModal = () => {
        toggle();
        setActiveArrowTab(1);
        handleFirstStepForm.resetForm();
        setPlacementsToSave([]);
    };

    const placementsToAddColumns = [
        {
            header: 'ID do Placement',
            accessorKey: 'adSpacePlacementId',
            enableSorting: true,
            enableColumnFilter: false,
        },
        {
            header: 'Nome do placement',
            accessorKey: 'name',
            enableSorting: true,
            enableColumnFilter: false,
        },
        {
            header: 'Tipo',
            accessorKey: 'tipo',
            cell: (cell: any) => {
                const tipo = cell.getValue();
                let badgeClass = 'badge-primary text-primary';
                if (tipo === 'display') badgeClass = 'badge-success text-success';
                else if (tipo === 'video') badgeClass = 'badge-warning text-warning';
                else if (tipo === 'audio') badgeClass = 'badge-info text-info';
                return <span className={`badge ${badgeClass}`}>{tipo}</span>;
            },
            enableSorting: true,
            enableColumnFilter: false,
        },
        {
            header: 'Sizes',
            accessorKey: 'sizes',
            cell: (cell: any) => {
                const row = cell.row.original;
                const actionCell: any[] = [];
                row.sizes?.forEach((size: string) => {
                    actionCell.push(<span
                        className="badge rounded-pill border border-primary text-primary">{size}</span>);
                });
                return <div className="d-flex gap-2">{actionCell}</div>;
            },
            enableSorting: true,
            enableColumnFilter: false,
        },
        {
            header: 'Tamanhos Expandidos',
            accessorKey: 'sizesExpandidos',
            cell: (cell: any) => {
                const row = cell.row.original;
                if (row.sizesExpandidos && row.sizesExpandidos.length > 0) {
                    const actionCell: any[] = [];
                    row.sizesExpandidos.forEach((sizeExpandido: string) => {
                        actionCell.push(<span
                            className="badge rounded-pill border border-secondary text-secondary">{sizeExpandido}</span>);
                    });
                    return <div className="d-flex gap-2">{actionCell}</div>;
                }
                return '-';
            },
            enableSorting: true,
            enableColumnFilter: false,
        },
        {
            header: 'Mídia Kit',
            accessorFn: (row: any) => {
                return row.mediaKitUrl || row.mediaKitFile;
            },
            cell: (cell: any) => {
                const url = cell.getValue();
                return url ? <>{url} <a href={url} target="_blank" rel="noopener noreferrer"><i className="ri-external-link-line"></i></a></> : '-';
            },
            enableSorting: true,
            enableColumnFilter: false,
        },
        {
            header: 'Peso Máximo para Rich Media',
            accessorKey: 'maxRichMediaSizeInKb',
            cell: (cell: any) => {
                const row = cell.row.original;
                return numeral(row.maxRichMediaSizeInKb * 1000).format() || '-';
            },
            enableSorting: true,
            enableColumnFilter: false,
        },
        {
            header: 'Peso Máximo para Imagem',
            accessorKey: 'maxImageSizeInKb',
            cell: (cell: any) => {
                const row = cell.row.original;
                return numeral(row.maxImageSizeInKb * 1000).format() || '-';
            },
            enableSorting: true,
            enableColumnFilter: false,
        },
        {
            header: 'Aceita Audio/Video',
            accessorKey: 'acceptsAudioVideo',
            cell: (cell: any) => {
                const row = cell.row.original;
                const className = row.acceptsAudioVideo ? 'badge-success text-success' : 'badge-danger text-danger';
                return <span className={`badge ${className}`}>{row.acceptsAudioVideo ? 'sim' : 'não'}</span>;
            },
            enableSorting: true,
            enableColumnFilter: false,
        },
        {
            header: 'Peso Máximo para Audio/Video',
            accessorKey: 'maxMediaSizeInKb',
            cell: (cell: any) => {
                const row = cell.row.original;
                return numeral(row.maxMediaSizeInKb * 1000).format() || '-';
            },
            enableSorting: true,
            enableColumnFilter: false,
        },
        {
            header: 'Tempo máximo de vídeo/audio (s)',
            assessorKey: 'maxMediaDurationInSeconds',
            cell: (cell: any) => {
                const row = cell.row.original;
                return row.maxMediaDurationInSeconds || '-';
            },
            enableSorting: true,
            enableColumnFilter: false,
        },
        {
            header: 'Recarregamento (s)',
            assessorKey: 'refreshInSeconds',
            cell: (cell: any) => {
                const row = cell.row.original;
                return row.refreshInSeconds || '-';
            },
            enableSorting: true,
            enableColumnFilter: false,
        },
        {
            header: 'Obs',
            accessorKey: 'notes',
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: 'Erros e Avisos',
            cell: (cell: any) => {
                const row = cell.row.original;
                const errors = row.errors.length === 0 ? '' : row.errors.map((error: string) => (
                    <span className="badge rounded-pill border border-danger text-danger">{error}</span>
                ));
                const warnings = row.warnings.length === 0 ? '' : row.warnings.map((warning: string) => (
                    <span className="badge rounded-pill border border-warning text-warning">{warning}</span>
                ));
                return (<>{errors}{warnings}</>);
            },
            enableSorting: true,
            enableColumnFilter: false,
        },
    ];

    if (userProfile?.role === 'ROLE_SUPER_ADMIN') {
        placementsToAddColumns.splice(1, 0, {
            header: 'Publisher',
            accessorFn: (row: any) => row.publisher?.name || '-',
            cell: (cell: any) => cell.getValue(),
            enableSorting: true,
            enableColumnFilter: false,
        });
    }

    const handleFirstStepForm: any = useFormik({
        // enableReinitialize : use this flag when initial values needs to be changed
        enableReinitialize: true,

        initialValues: {
            file: undefined,
        },

        validationSchema: Yup.object({
            file: Yup.mixed().required('É necessário um arquivo csv'),
            // TODO: As validações abaixo não estão funcionando
            // .test(
            //     "fileFormat",
            //     "Placement de arquivo inválido",
            //     (value: any) => value && SUPPORTED_FORMATS.includes(value.type)
            // )
            // .test(
            //     "fileSize",
            //     "O arquivo é muito grande",
            //     (value: any) => value && value.size <= 2000000
            // ),
        }),
        onSubmit: () => {
            setFirstStepImportButtonLoader(true);
            const formData = new FormData();
            const file: HTMLInputElement | null = document.querySelector('#file-field');
            if (file && file.files) {
                formData.append('file', file.files[0]);
            }
            return importPlacements(formData)
                .then((res: any) => {
                    setPlacementsToSave(res);
                    setActiveArrowTab(2);
                })
                .catch(() => {
                })
                .finally(() => {
                    setFirstStepImportButtonLoader(false);
                });
        },
    });

    // Second Step Form Validation
    const handleSecondStepForm = (event: any) => {
        event.preventDefault();
        const placementsToSaveCount = placementsToSave.length;
        if (!window.confirm(`Deseja realmente importar ${placementsToSaveCount} placements?`)) {
            return;
        }

        const countWarnings = placementsToSave.filter((placement) => placement.warnings.length !== 0).length;
        if (countWarnings) {
            if (!window.confirm(`Existem ${countWarnings} placements com alertas. Deseja seguir mesmo assim?`)) {
                return;
            }
        }
        const countErrors = placementsToSave.filter((placement) => placement.errors.length !== 0).length;
        if (countErrors) {
            window.alert(`Existem ${countErrors} placements com erros. Não é possível importar placements com erros.`);
            return;
        }

        setSecondStepImportButtonLoader(true);

        confirmImportPlacements(placementsToSave)
            .then((res: any) => {
                toggleImportPlacementModal();
                toast.success('Blocos de anúncios importados com sucesso!');
                onAddData(res);
            })
            .catch(() => {
            })
            .finally(() => {
                setSecondStepImportButtonLoader(false);
            });
    };

    return (
        <Modal id="showModal" isOpen={isOpen} toggle={toggleImportPlacementModal} fade={false} keyboard={true}
               backdrop={false} centered size={'xl'}>
            <ModalHeader toggle={toggleImportPlacementModal} className="bg-light p-3">
                Importar blocos de anúncios
            </ModalHeader>

            <ModalBody>
                <div className="container-fluid">
                    <div className="row">
                        <div className="step-arrow-nav mb-4">
                            <Nav
                                className="nav-pills custom-nav nav-justified"
                                role="tablist"
                            >
                                <NavItem>
                                    <NavLink
                                        href="#"
                                        id="steparrow-gen-info-tab"
                                        className={`${activeArrowTab === 1 ? 'active' : ''} ${activeArrowTab >= 2 ? 'done' : ''}`}
                                        onClick={() => {
                                            if (activeArrowTab === 2) {
                                                setActiveArrowTab(1);
                                            }
                                        }}
                                    >
                                        Importe
                                    </NavLink>
                                </NavItem>
                                <NavItem>
                                    <NavLink
                                        href="#"
                                        id="steparrow-gen-info-tab"
                                        className={`${activeArrowTab === 2 ? 'active' : ''} ${activeArrowTab >= 3 ? 'done' : ''}`}
                                    >
                                        Confirme
                                    </NavLink>
                                </NavItem>
                                <NavItem>
                                    <NavLink
                                        href="#"
                                        id="steparrow-gen-info-tab"
                                        className={activeArrowTab === 3 ? 'active' : ''}
                                    >
                                        Concluído
                                    </NavLink>
                                </NavItem>
                            </Nav>
                        </div>
                    </div>
                    <div className="row">
                        <TabContent activeTab={activeArrowTab}>
                            <TabPane id="step-1" tabId={1}>
                                <Form onSubmit={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleFirstStepForm.handleSubmit();
                                    return false;
                                }}>
                                    <div>
                                        <div className="mb-3">
                                            <Label
                                                htmlFor="file-field"
                                                className="form-label"
                                            >
                                                Blocos de anúncios - CSV
                                            </Label>
                                            <Input
                                                className="form-control"
                                                type="file"
                                                id="file-field"
                                                name="file"
                                                accept=".csv"
                                                onChange={handleFirstStepForm.handleChange}
                                                onBlur={handleFirstStepForm.handleBlur}
                                                value={handleFirstStepForm.values.file}
                                                invalid={
                                                    !!(handleFirstStepForm.touched.file && handleFirstStepForm.errors.file)
                                                }
                                            />
                                            {handleFirstStepForm.touched.file && handleFirstStepForm.errors.file ? (
                                                <FormFeedback
                                                    type="invalid">{handleFirstStepForm.errors.file}</FormFeedback>
                                            ) : null}
                                        </div>
                                    </div>
                                    <div className="d-flex align-items-start gap-3 mt-4">
                                        <button
                                            type="submit"
                                            disabled={firstStepImportButtonLoader}
                                            className="btn btn-success btn-label right ms-auto nexttab nexttab"
                                        >
                                            <i className="ri-arrow-right-line label-icon align-middle fs-16 ms-2"></i>
                                            {firstStepImportButtonLoader && <Spinner size="sm"
                                                                                     className="me-2"> Carregando... </Spinner>}
                                            Importar
                                        </button>
                                    </div>
                                </Form>
                            </TabPane>

                            <TabPane id="step-2" tabId={2}>
                                <TableContainer
                                    divClass="table-responsive"
                                    columns={placementsToAddColumns}
                                    data={placementsToSave}
                                    isGlobalFilter={true}
                                    customPageSize={30}
                                    SearchPlaceholder="Procurar..."
                                />

                                <div className="d-flex align-items-start gap-3 mt-4">
                                    <button
                                        type="button"
                                        className="btn btn-light btn-label previestab"
                                        onClick={() => {
                                            setActiveArrowTab(activeArrowTab - 1);
                                        }}
                                    >
                                        <i className="ri-arrow-left-line label-icon align-middle fs-16 me-2"></i>
                                        Cancelar & Voltar
                                    </button>
                                    <button
                                        type="button"
                                        disabled={secondStepImportButtonLoader}
                                        onClick={handleSecondStepForm}
                                        className="btn btn-success btn-label right ms-auto nexttab nexttab"
                                    >
                                        <i className="ri-arrow-right-line label-icon align-middle fs-16 ms-2"></i>
                                        {secondStepImportButtonLoader && <Spinner size="sm"
                                                                                  className="me-2"> Carregando... </Spinner>}
                                        Confirmar
                                    </button>
                                </div>
                            </TabPane>

                            <TabPane id="step-3" tabId={3}>
                                <div className="text-center">
                                    <div className="avatar-md mt-5 mb-4 mx-auto">
                                        <div
                                            className="avatar-title bg-light text-success display-4 rounded-circle">
                                            <i className="ri-checkbox-circle-fill"></i>
                                        </div>
                                    </div>
                                    <h5>Muito bom !</h5>
                                    <p className="text-muted">
                                        Os blocos de anúncios foram importados com sucesso.
                                    </p>
                                    <p>&nbsp;</p>
                                    <button
                                        type="button"
                                        className="btn btn-success btn-label right ms-auto nexttab nexttab"
                                        onClick={toggleImportPlacementModal}
                                    >
                                        <i className="ri-close-line label-icon align-middle fs-16 ms-2"></i>
                                        Fechar
                                    </button>
                                </div>
                            </TabPane>
                        </TabContent>
                    </div>
                </div>
            </ModalBody>
        </Modal>
    );
};

export default ImportPlacementModal;